/**
 * Patient Database Page
 *
 * Main landing page for the MedHx Builder. Shows a searchable patient
 * database with history log status, document counts, and quick actions.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Users,
  FileText,
  RefreshCw,
  AlertCircle,
  Filter,
} from 'lucide-react';
import { useMHR } from '../contexts';
import { PatientCard } from '../components/PatientCard';
import { PageHeader } from '../../ui';
import * as mhrApi from '../lib/api';
import type { PatientWithStats } from '../types';

export function PatientDatabase() {
  const navigate = useNavigate();
  const { error, clearError } = useMHR();

  // Patient data
  const [patients, setPatients] = useState<PatientWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // Search/filter
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterHasLog, setFilterHasLog] = useState<'all' | 'yes' | 'no'>('all');

  // Add patient form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPatientFirstName, setNewPatientFirstName] = useState('');
  const [newPatientLastName, setNewPatientLastName] = useState('');
  const [newPatientDob, setNewPatientDob] = useState('');
  const [addingPatient, setAddingPatient] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Load patients
  const loadPatients = useCallback(async () => {
    setLoading(true);
    try {
      const result = await mhrApi.listPatientsWithStats({
        search: searchQuery || undefined,
        limit: 50,
      });
      setPatients(result.data);
      setTotal(result.total);
    } catch (err: any) {
      console.error('[PatientDatabase] Error loading patients:', err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  // Filter patients locally
  const filteredPatients = useMemo(() => {
    return patients.filter(p => {
      if (filterHasLog === 'yes' && !p.history_log_id) return false;
      if (filterHasLog === 'no' && p.history_log_id) return false;
      return true;
    });
  }, [patients, filterHasLog]);

  // Stats
  const stats = useMemo(() => ({
    total: patients.length,
    withLogs: patients.filter(p => p.history_log_id).length,
    totalDocuments: patients.reduce((sum, p) => sum + (p.document_count || 0), 0),
  }), [patients]);

  // Add patient handler
  const handleAddPatient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPatientFirstName.trim() || !newPatientLastName.trim() || !newPatientDob) return;

    setAddingPatient(true);
    setAddError(null);
    try {
      const patient = await mhrApi.createPatient({
        firstName: newPatientFirstName.trim(),
        lastName: newPatientLastName.trim(),
        dob: newPatientDob,
      });
      setShowAddForm(false);
      setNewPatientFirstName('');
      setNewPatientLastName('');
      setNewPatientDob('');
      // Navigate to the new patient chart
      navigate(`/mhr/patients/${patient.id}`);
    } catch (err: any) {
      setAddError(err.response?.data?.error || 'Failed to create patient');
    } finally {
      setAddingPatient(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Error alert */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
          <button onClick={clearError} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300" aria-label="Dismiss error">
            &times;
          </button>
        </div>
      )}

      {/* Header */}
      <PageHeader
        title="MedHx Builder"
        icon={Users}
        subtitle="Build and manage patient medical history logs"
        actions={
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
          >
            <Plus size={18} />
            Add Patient
          </button>
        }
      />

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
              <Users className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Patients</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
              <FileText className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">With History Logs</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.withLogs}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
              <FileText className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Documents</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.totalDocuments}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Add patient inline form */}
      {showAddForm && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-primary-200 dark:border-primary-800 p-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Add New Patient</h3>
          <form onSubmit={handleAddPatient} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">First Name *</label>
                <input
                  type="text"
                  value={newPatientFirstName}
                  onChange={e => setNewPatientFirstName(e.target.value)}
                  placeholder="First name"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Last Name *</label>
                <input
                  type="text"
                  value={newPatientLastName}
                  onChange={e => setNewPatientLastName(e.target.value)}
                  placeholder="Last name"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date of Birth *</label>
                <input
                  type="date"
                  value={newPatientDob}
                  onChange={e => setNewPatientDob(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              A unique MRN will be auto-generated. All PHI is encrypted at rest.
            </p>
            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={addingPatient || !newPatientFirstName.trim() || !newPatientLastName.trim() || !newPatientDob}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors min-h-[44px] sm:min-h-0"
              >
                {addingPatient ? 'Creating...' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => { setShowAddForm(false); setAddError(null); }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors min-h-[44px] sm:min-h-0"
              >
                Cancel
              </button>
            </div>
          </form>
          {addError && (
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">{addError}</p>
          )}
        </div>
      )}

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            placeholder="Search by name, DOB, MRN, or ID..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              showFilters
                ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            <Filter size={18} />
            Filters
          </button>
          <button
            onClick={loadPatients}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 min-h-[44px] sm:min-h-0"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Filter options */}
      {showFilters && (
        <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-4 flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">History Log</label>
            <select
              value={filterHasLog}
              onChange={e => setFilterHasLog(e.target.value as 'all' | 'yes' | 'no')}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All Patients</option>
              <option value="yes">Has History Log</option>
              <option value="no">No History Log</option>
            </select>
          </div>
        </div>
      )}

      {/* Patient grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : filteredPatients.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <Users className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No patients found</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchQuery || filterHasLog !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Get started by adding your first patient'}
          </p>
          {!searchQuery && filterHasLog === 'all' && (
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors min-h-[44px] sm:min-h-0"
            >
              <Plus size={20} />
              Add Patient
            </button>
          )}
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {filteredPatients.length} of {total} patient{total !== 1 ? 's' : ''}
          </p>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredPatients.map(patient => (
              <PatientCard
                key={patient.id}
                patient={patient}
                onClick={() => navigate(`/mhr/patients/${patient.id}`)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
